/**
 * Mixpanel / anonymous-profile traits by tenant (`env.brand`).
 * New trait keys use English Title Case for consistency with Segment/Mixpanel conventions.
 */

import getPassengerAge from 'utils/getPassengerAge';
import { formatName } from 'utils/purchase/parsePassenger';

/** Title Case keys for traits introduced with purchase-profile analytics. */
export const PURCHASE_PROFILE_TRAIT_KEYS = {
  DOCUMENT_TYPE: 'Document Type',
  DOCUMENT_ID: 'Document ID',
  DATE_OF_BIRTH: 'Date of Birth',
  AGE: 'Age',
  GENDER: 'Gender',
  NATIONALITY: 'Nationality',
};

/** Cruz del Sur — first passenger profile (document, DOB, gender, nationality). */
export const TENANTS_FIRST_PASSENGER_PROFILE_TRAITS = new Set(['cds']);

/**
 * Whether first-passenger profile traits may be sent for a tenant brand (CDS only).
 *
 * @param {string} [brand] - Whitelabel brand key (`env.brand`).
 * @returns {boolean}
 */
export function isFirstPassengerProfileTraitsAllowed(brand) {
  return Boolean(brand) && TENANTS_FIRST_PASSENGER_PROFILE_TRAITS.has(brand);
}

/**
 * Whether purchaser document traits may be sent for a tenant brand (all tenants except CDS).
 *
 * @param {string} [brand] - Whitelabel brand key (`env.brand`).
 * @returns {boolean}
 */
export function isPurchaserDocumentProfileTraitsAllowed(brand) {
  return Boolean(brand) && !TENANTS_FIRST_PASSENGER_PROFILE_TRAITS.has(brand);
}

/**
 * Merge passenger profile sources and normalize API / form field names for trait mapping.
 *
 * @param {...Object|null|undefined} sources - Passenger objects; later sources override earlier ones.
 * @returns {Object|null} Normalized passenger fields, or null when no source is provided.
 */
export function resolvePassengerProfileSource(...sources) {
  const merged = sources
    .filter((source) => source && typeof source === 'object')
    .reduce((acc, source) => ({ ...acc, ...source }), {});

  if (!Object.keys(merged).length) {
    return null;
  }

  const documentNumber = merged.document || merged.documentId || '';

  return {
    ...merged,
    document: documentNumber,
    documentId: merged.documentId || documentNumber,
    documentType: merged.documentType || merged.document_type || '',
    dateOfBirth: merged.dateOfBirth || merged.birthDate || merged.birthday || '',
    gender: merged.gender || '',
    nationality: merged.nationality || merged.isoCountryCode || '',
  };
}

/**
 * Normalize full name for equality checks (buyer vs first passenger).
 *
 * @param {Object} nameFields - firstName, lastName, secondLastName.
 * @returns {string}
 */
function comparableFullName(nameFields) {
  const firstName = String(nameFields?.firstName ?? '').trim();
  if (!firstName) {
    return '';
  }

  const { name } = formatName(nameFields);
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Whether purchaser fields refer to the same person as the first passenger (checkout inference).
 *
 * @param {Object|null|undefined} purchaser - purchaserFirstName, purchaserLastName, purchaserSecondLastName, documentType, documentId, email.
 * @param {Object|null|undefined} passenger - firstName, lastName, secondLastName, documentType, document / documentId, email.
 * @returns {boolean}
 */
export function isPurchaserSameAsFirstPassenger(purchaser, passenger) {
  if (!purchaser || !passenger || typeof purchaser !== 'object' || typeof passenger !== 'object') {
    return false;
  }

  const purchaserName = comparableFullName({
    firstName: purchaser.purchaserFirstName,
    lastName: purchaser.purchaserLastName,
    secondLastName: purchaser.purchaserSecondLastName,
  });
  const passengerName = comparableFullName({
    firstName: passenger.firstName,
    lastName: passenger.lastName,
    secondLastName: passenger.secondLastName,
  });

  if (!purchaserName || !passengerName || purchaserName !== passengerName) {
    return false;
  }

  const purchaserDoc = String(purchaser.documentId || '').trim();
  const passengerDoc = String(passenger.document || passenger.documentId || '').trim();
  if (purchaserDoc && passengerDoc && purchaserDoc !== passengerDoc) {
    return false;
  }

  const purchaserDocType = String(purchaser.documentType || '').trim();
  const passengerDocType = String(passenger.documentType || '').trim();
  if (
    purchaserDocType &&
    passengerDocType &&
    purchaserDocType.toLowerCase() !== passengerDocType.toLowerCase()
  ) {
    return false;
  }

  const purchaserEmail = String(purchaser.email || '')
    .trim()
    .toLowerCase();
  const passengerEmail = String(passenger.email || '')
    .trim()
    .toLowerCase();
  if (purchaserEmail && passengerEmail && purchaserEmail !== passengerEmail) {
    return false;
  }

  return true;
}

/**
 * First passenger traits (Cruz del Sur).
 *
 * @param {Object|null|undefined} passenger - First passenger form or purchase payload.
 * @param {string} [brand] - Whitelabel brand key; required for PII to be included.
 * @returns {Object} Traits object.
 */
export function getFirstPassengerProfileTraits(passenger, brand) {
  const normalizedPassenger = resolvePassengerProfileSource(passenger);
  if (!isFirstPassengerProfileTraitsAllowed(brand) || !normalizedPassenger) {
    return {};
  }

  const traits = {};
  const traitKeys = PURCHASE_PROFILE_TRAIT_KEYS;

  if (normalizedPassenger.documentType) {
    traits[traitKeys.DOCUMENT_TYPE] = normalizedPassenger.documentType;
  }
  const documentNumber = normalizedPassenger.document || normalizedPassenger.documentId;
  if (documentNumber) {
    traits[traitKeys.DOCUMENT_ID] = documentNumber;
  }
  if (normalizedPassenger.dateOfBirth) {
    traits[traitKeys.DATE_OF_BIRTH] = normalizedPassenger.dateOfBirth;
    const age = getPassengerAge(normalizedPassenger.dateOfBirth);
    if (typeof age === 'number' && age >= 0 && Number.isFinite(age)) {
      traits[traitKeys.AGE] = age;
    }
  }
  if (normalizedPassenger.gender) {
    traits[traitKeys.GENDER] = normalizedPassenger.gender;
  }
  if (normalizedPassenger.nationality) {
    traits[traitKeys.NATIONALITY] = normalizedPassenger.nationality;
  }

  return traits;
}

/**
 * Purchaser document traits (form uses `documentId` for the number).
 *
 * @param {Object|null|undefined} purchaser - Purchaser fields from redux-form or submit payload.
 * @param {string} [brand] - Whitelabel brand key; required for PII to be included.
 * @returns {Object} Traits object.
 */
export function getPurchaserDocumentProfileTraits(purchaser, brand) {
  if (
    !isPurchaserDocumentProfileTraitsAllowed(brand) ||
    !purchaser ||
    typeof purchaser !== 'object'
  ) {
    return {};
  }

  const traits = {};
  const traitKeys = PURCHASE_PROFILE_TRAIT_KEYS;

  if (purchaser.documentType) {
    traits[traitKeys.DOCUMENT_TYPE] = purchaser.documentType;
  }
  if (purchaser.documentId) {
    traits[traitKeys.DOCUMENT_ID] = purchaser.documentId;
  }

  return traits;
}

/**
 * Combined purchase-profile PII traits for identifyUser / createAnonymousProfile.
 *
 * @param {string} brand - Whitelabel brand key (`env.brand`).
 * @param {Object} [sources] - passenger, passengerFromForm, purchaser.
 * @param {Object|null|undefined} [sources.passenger] - First passenger from purchase payload.
 * @param {Object|null|undefined} [sources.passengerFromForm] - First passenger from redux-form (preferred).
 * @param {Object|null|undefined} [sources.purchaser] - Purchaser fields.
 * @returns {Object} Traits object (empty when not brand-gated).
 */
export function getPurchaseProfileAnalyticsTraits(
  brand,
  { passenger, passengerFromForm, purchaser } = {},
) {
  if (!brand) {
    return {};
  }

  if (isFirstPassengerProfileTraitsAllowed(brand)) {
    const passengerProfile = resolvePassengerProfileSource(passenger, passengerFromForm);
    return getFirstPassengerProfileTraits(passengerProfile, brand);
  }

  return getPurchaserDocumentProfileTraits(purchaser, brand);
}
